import {
  Checkbox,
  CircularProgressWithLabel,
  Iconify,
  LoadingContainer,
  Text,
  useFileUrlToTypeMapping,
} from '@pflegenavi/web-components';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import type { Theme } from '@mui/material/styles';
import {
  Card,
  FormControlLabel,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  useReceiptImages,
  useUploadReceiptImage,
} from '@pflegenavi/frontend/api-nursing-home';
import type { FormikProps } from 'formik';
import { v4 } from 'uuid';
import { ImagesHandlingForm } from './ImagesHandlingForm';
import { HiddenInputForReceiptFile } from './HiddenInputForReceiptFile';
import type { ConnectDropTarget } from 'react-dnd';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import type {
  UploadFile,
  UploadState,
} from '@pflegenavi/shared-frontend/platform';
import { computeMd5HashOfFile } from '@pflegenavi/shared-web/hash-utils';

interface UploadReceiptCardProps<
  V extends { receiptImageIds: string[]; receipt_not_available: boolean }
> {
  formik: FormikProps<V>;
  disabled?: boolean;
  uploadReceipt: UploadReceiptHookReturn;
  allowWithoutImage?: boolean;
}

export type FilePreviewType =
  | {
      preview?: string | null;
      name?: string;
    }
  | undefined;

export const UploadReceiptCard = <
  V extends {
    receiptImageIds: string[];
    receipt_not_available: boolean;
  }
>({
  formik,
  disabled = true,
  uploadReceipt,
  allowWithoutImage = true,
}: UploadReceiptCardProps<V>): JSX.Element => {
  const inputRef = createRef<HTMLInputElement>();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [changingFile, setChangingFile] = useState(false);
  const { t } = useTranslation();

  const receiptImageIds = formik.values['receiptImageIds'];

  const {
    data: receiptImageUrlsMap,
    isLoading,
    isError,
  } = useReceiptImages(receiptImageIds);

  const receiptImageUrls: string[] | undefined = useMemo(() => {
    if (receiptImageUrlsMap) {
      return Object.values(receiptImageUrlsMap)
        .filter((item) => item !== undefined)
        .map((item) => item?.toString() ?? '');
    }
    return undefined;
  }, [receiptImageUrlsMap]);

  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  const onOpenSelectImageDialog = useCallback(() => {
    inputRef?.current?.click();
  }, [inputRef]);

  const { setFieldError, setFieldValue, setSubmitting } = formik;

  const { uploadState, canDrop, isOver, setFile, file, upload } = uploadReceipt;

  useEffect(() => {
    if (uploadState?.error) {
      setFieldError('receiptImageIds', 'Failed to upload receipt');
      setFieldValue('receiptImageIds', []);
      setSubmitting(false);
    }
  }, [uploadState?.error, setFieldError, setFieldValue, setSubmitting]);

  useEffect(() => {
    if (uploadState?.finished) {
      if (changingFile) {
        if (uploadState && uploadState.receiptImageId) {
          const newReceiptsUrls = [...receiptImageIds];
          newReceiptsUrls[selectedImageIndex] = uploadState.receiptImageId;
          setFieldValue('receiptImageIds', newReceiptsUrls);
          setChangingFile(false);
        }
      } else {
        setFieldValue('receiptImageIds', [
          ...receiptImageIds,
          uploadState?.receiptImageId,
        ]);
        setSelectedImageIndex(receiptImageIds.length);
      }
      setSubmitting(false);
    }
    // TODO many dependencies missing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uploadState?.finished,
    uploadState?.receiptImageId,
    setFieldValue,
    setSubmitting,
  ]);

  const onDeleteFile = useCallback(() => {
    const newImagesIds = [...receiptImageIds];
    newImagesIds.splice(selectedImageIndex, 1);
    setFieldValue('receiptImageIds', newImagesIds);
    setSelectedImageIndex(0);
  }, [receiptImageIds, selectedImageIndex, setFieldValue]);

  const onSelectPrevious = () => {
    setSelectedImageIndex(selectedImageIndex - 1);
  };

  const onSelectNext = () => {
    setSelectedImageIndex(selectedImageIndex + 1);
  };

  const onSelectDifferentImage = (imageUrl: string) => {
    const imageIndex = receiptImageUrls?.indexOf(imageUrl);
    if (imageIndex !== undefined && imageIndex !== -1) {
      setSelectedImageIndex(imageIndex);
    }
  };

  const onChangeFile = () => {
    onOpenSelectImageDialog();
    setChangingFile(true);
  };

  const onAddFile = useCallback(() => {
    const reader = new FileReader();
    const file = inputRef?.current?.files?.[0];

    reader.onload = function (e) {
      const preview = e?.target?.result;
      if (preview instanceof ArrayBuffer) {
        throw new Error('Unexpected array buffer');
      }
      setFile({ preview, name: file?.name });
    };

    if (file) {
      formik.setSubmitting(true);
      upload(file);
      reader.readAsDataURL(file);
      return;
    }
    setChangingFile(false);
  }, [formik, inputRef, upload, setFile]);

  const isActive = canDrop && isOver;

  if (isError) {
    return <Typography>{t('receipt.failed-to-load-images')}</Typography>;
  }

  if (uploadState !== undefined && !uploadState.finished) {
    return (
      <FileChosenContainer center={true} sx={{ justifyContent: 'center' }}>
        <CircularProgressWithLabel
          value={uploadState?.progress ? uploadState?.progress * 100 : 0}
          color="secondary"
          size={120}
        />
      </FileChosenContainer>
    );
  }

  if (receiptImageUrls && receiptImageUrls?.length > 0) {
    return (
      <FileChosenContainer>
        <ImagesHandlingForm
          canEdit={!disabled}
          file={file}
          setFile={setFile}
          receiptImageUrls={receiptImageUrls}
          imageUrlToTypeMap={imageUrlToTypeMap}
          formik={formik}
          inputRef={inputRef}
          onAddFile={onAddFile}
          onChangeFile={onChangeFile}
          onOpenSelectImageDialog={onOpenSelectImageDialog}
          onDeleteFile={onDeleteFile}
          selectedImageIndex={selectedImageIndex}
          onSelectPrevious={onSelectPrevious}
          onSelectNext={onSelectNext}
          onSelectDifferentImage={onSelectDifferentImage}
        />
      </FileChosenContainer>
    );
  }

  return (
    <Card sx={{ p: 3, flex: 1 }}>
      {isLoading && receiptImageIds.length !== 0 ? (
        <LoadingContainer height={200} />
      ) : (
        <Stack direction="column" sx={{ height: 1, justifyContent: 'center' }}>
          <RootStyle>
            <HiddenInputForReceiptFile
              onChangeFile={onAddFile}
              inputRef={inputRef}
            />
            <DropZoneStyle
              onClick={disabled ? undefined : onOpenSelectImageDialog}
              sx={{
                ...(isActive && { opacity: 0.72 }),
                ...(disabled && {
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  '&:hover': { cursor: 'not-allowed' },
                }),
              }}
            >
              <PlaceholderStyle
                className="placeholder"
                hide={receiptImageIds.length !== 0}
              >
                <Iconify
                  icon={'ic:round-add-a-photo'}
                  sx={{ width: 24, height: 24, mb: 1 }}
                />
                <Text variant="caption">{t('receipt.upload.title')}</Text>
              </PlaceholderStyle>
            </DropZoneStyle>
          </RootStyle>

          <MaxSizeText variant="caption">
            <Trans
              i18nKey="receipt.max-file-size"
              components={{
                b: <br />,
              }}
            />
          </MaxSizeText>
          {allowWithoutImage && (
            <FormControlLabel
              sx={{
                margin: 'auto',
              }}
              control={
                <Checkbox
                  checked={formik.values.receipt_not_available}
                  disabled={disabled}
                  onChange={(_event, value) =>
                    formik.setFieldValue('receipt_not_available', value)
                  }
                />
              }
              label={t('receipt.upload.withoutImage')}
            />
          )}
        </Stack>
      )}
    </Card>
  );
};

interface UploadReceiptHookProps {
  formik: FormikProps<any>;
}

interface UploadReceiptHookReturn {
  file: any;
  setFile: (file: any) => void;
  uploadState?: UploadState;
  canDrop: boolean;
  isOver: boolean;
  drop: ConnectDropTarget;
  upload: UploadFile;
}

export const useUploadReceipt = ({
  formik,
}: UploadReceiptHookProps): UploadReceiptHookReturn => {
  const { upload, uploadState } = useUploadReceiptImage(
    v4,
    computeMd5HashOfFile
  );
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<any>(null);

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: any[] }) {
        if (setFile) {
          const file = item.files[0];
          if (!(file instanceof File)) {
            enqueueSnackbar(t('common.drop-file-from-computer'), {
              variant: 'error',
            });
            return;
          }
          setFile({ preview: URL.createObjectURL(file) });
          formik.setSubmitting(true);
          upload(file);

          return;
        }
      },
      collect: (monitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
    }),
    [enqueueSnackbar, t]
  );

  return {
    file,
    setFile,
    upload,
    uploadState,
    canDrop,
    isOver,
    drop,
  };
};

const RootStyle = styled('div')(({ theme }: { theme: Theme }) => ({
  width: 144,
  height: 144,
  marginLeft: 'auto',
  marginRight: 'auto',
  borderRadius: '50%',
  padding: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
}));

export const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '50%',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9,
    },
  },
});

interface PlaceholderProps {
  hide: boolean;
}

export const PlaceholderStyle = styled('div')<PlaceholderProps>(
  ({ theme, hide }) => ({
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.neutral,
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': { opacity: 0.72 },
    ...(hide && {
      opacity: 0,
      color: 'common.white',
      bgcolor: 'grey.900',
      '&:hover': { opacity: 0.72 },
    }),
  })
);

interface FileChosenContainerProps {
  center?: boolean;
}

const FileChosenContainer = styled(Card)<FileChosenContainerProps>(
  ({ center }) =>
    ({ theme }) =>
      theme.unstable_sx({
        p: 1,
        flex: 1,
        display: 'flex',
        overflow: 'visible',
        alignItems: center ? 'center' : 'flex-start',
      })
);

const MaxSizeText = styled(Text)(({ theme }) =>
  theme.unstable_sx({
    mt: 2,
    mx: 'auto',
    display: 'block',
    textAlign: 'center',
    color: 'text.secondary',
  })
);
